/**
 * Heavily inspired by https://stash.atlassian.com/projects/AID/repos/aid-frontend/browse/src/components/PasswordStrengthMeter.tsx.
 */
import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { debounce } from 'lodash';
import makeAsyncScriptLoader from 'react-async-script';
import type { IntlShape } from 'react-intl-next';
import * as fonts from 'view/styles/fonts';
import * as grid from 'view/styles/grid';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { MIN_PASSWORD_LENGTH_IN_CHARS } from '@atlassian/help-center-common-util/sign-up-form-validator';
import messages from './messages';
const passwordAnalyzerUrl = 'https://cdnjs.cloudflare.com/ajax/libs/zxcvbn/4.4.2/zxcvbn.js';
const splitter = /\W+/;

const STRENGTH_SCORE_DEBOUNCE_IN_MILLIS = 200;

type PasswordStrengthScore = 0 | 1 | 2 | 3 | 4;

interface PasswordAnalyzerResult {
    score: PasswordStrengthScore;
}

export type PasswordAnalyzer = (password: string, userInputs?: string[]) => PasswordAnalyzerResult;

interface Props {
    intl: IntlShape;
    scoreWords: string[];
    password: string;
    zxcvbn?: PasswordAnalyzer;
}

interface State {
    score: PasswordStrengthScore | null;
}

export class PasswordStrengthMeter extends React.Component<Props, State> {
    state = {
        score: null,
    };

    calculateScoreDebounced = debounce(() => {
        const { password, scoreWords, zxcvbn: passwordAnalyzer } = this.props;
        const result = passwordAnalyzer && password ? passwordAnalyzer(password, scoreWords) : null;
        this.setState({
            score: result ? result.score : null,
        });
    }, STRENGTH_SCORE_DEBOUNCE_IN_MILLIS);

    componentDidUpdate = (prevProps: Props) => {
        if (this.props.password === prevProps.password) {
            return;
        }

        if (!this.props.password) {
            this.setState({
                score: null,
            });

            return;
        }

        this.calculateScoreDebounced();
    };

    getStrengthTextForScore = (score: number | null) => {
        const { intl, password, zxcvbn: passwordAnalyzer } = this.props;

        if (!password) {
            return '';
        }

        if (password.length < MIN_PASSWORD_LENGTH_IN_CHARS) {
            return this.props.intl.formatMessage(messages.passwordInvalid);
        }

        if (!passwordAnalyzer) {
            return intl.formatMessage(messages.passwordStrengthUnavailable);
        }

        return score !== null ? this.mapScoreToStrengthText(score) : '';
    };

    mapScoreToStrengthText = (score: number): string => {
        let strengthText;
        switch (score) {
            case 0:
                strengthText = messages.passwordStrengthLevel0;
                break;
            case 1:
                strengthText = messages.passwordStrengthLevel1;
                break;
            case 2:
                strengthText = messages.passwordStrengthLevel2;
                break;
            case 3:
                strengthText = messages.passwordStrengthLevel3;
                break;
            case 4:
                strengthText = messages.passwordStrengthLevel4;
                break;
            default:
                strengthText = messages.passwordStrengthUnavailable;
        }
        return this.props.intl.formatMessage(strengthText);
    };

    render() {
        const { score } = this.state;
        const strengthText = this.getStrengthTextForScore(score);

        return (
            <>
                <Bar data-testid="password-strength-bar" score={score}>
                    <Dividers />
                </Bar>
                <Hint data-testid="password-strength-hint">{strengthText}</Hint>
            </>
        );
    }
}

// Suppressing existing violation. Please fix this.
// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment
export const AsyncLoadedPasswordStrengthMeter = makeAsyncScriptLoader(passwordAnalyzerUrl, {
    globalName: 'zxcvbn',
})(PasswordStrengthMeter);

export default AsyncLoadedPasswordStrengthMeter;

const addUnique = (seen: string[], chunk: string) => (seen.includes(chunk) ? seen : [...seen, chunk]);

export function scoreWordSplitter(strings: string[]) {
    const deduped = strings.reduce(addUnique, []);
    return deduped.reduce((arr, str) => str.split(splitter).reduce(addUnique, arr), deduped);
}

const leftPercentage = '19%';
const rightPercentage = '19%';
const leftPercentageAfter = '30%';
const rightPercentageAfter = '30%';

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Dividers = styled.span`
    border-left: 5px solid ${token('color.border.inverse', colors.N0)};
    border-right: 5px solid ${token('color.border.inverse', colors.N0)};
    bottom: 0;
    left: ${leftPercentage};
    position: absolute;
    right: ${rightPercentage};
    top: 0;

    &::after {
        border-left: 5px solid ${token('color.border.inverse', colors.N0)};
        border-right: 5px solid ${token('color.border.inverse', colors.N0)};
        bottom: 0;
        content: '';
        left: ${leftPercentageAfter};
        position: absolute;
        right: ${rightPercentageAfter};
        top: 0;
    }
`;

const barWidths = ['20%', '40%', '60%', '80%', '100%'];
const barColours = [
    token('color.chart.danger.bold', colors.R500),
    token('color.chart.danger', colors.R300),
    token('color.chart.warning', colors.Y300),
    token('color.chart.success', colors.G300),
    token('color.chart.success.bold', colors.G400),
];

interface BarProps {
    score: number | null;
}

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const Bar = styled.div<BarProps>`
    background: ${token('color.chart.neutral', colors.N40)};
    height: ${grid.multiple(0.25).px};
    margin-top: ${token('space.075', '6px')};
    position: relative;

    &::before {
        /* stylelint-disable scale-unlimited/declaration-strict-value */
        background-color: ${(props) =>
            props.score === null ? token('color.chart.neutral', colors.N40) : barColours[props.score]};
        bottom: 0;
        content: '';
        left: 0;
        position: absolute;
        top: 0;
        transition: all 0.2s ease-out;
        width: ${(props) => (props.score === null ? 0 : barWidths[props.score])};
    }
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const Hint = styled.p`
    color: ${token('color.text.subtlest', colors.N90)};
    ${fonts.small}
    height: ${grid.multiple(2).px};
    line-height: ${grid.multiple(2).px};
    margin-top: ${token('space.050', '4px')};
    position: relative;
    text-align: center;
`;
